import {
  DEFAULT_SEGMENTS,
  MS_IN_HOUR,
  MS_IN_MINUTE,
  MS_IN_SECOND,
  SEGMENT_DIGITS,
  SEGMENT_TEXT_LENGTH,
} from "./constants";
import { CustomTimeValue, SegmentValues, TimeSegmentKey } from "./types";

/* #region small utils */

export const pad2 = (value: number): string => String(value).padStart(2, "0");

export const clamp = (value: number, min: number, max: number): number =>
  Math.min(Math.max(value, min), max);

const chunkDigits = (digits: string): string[] => {
  const groups: string[] = [];
  for (let i = 0; i < digits.length; i += 2) {
    groups.push(digits.slice(i, i + 2));
  }
  return groups;
};

/** All selectable values of one column, e.g. 0, 5, 10 ... 55 for a 5 minute step. */
export const buildOptions = (max: number, step: number): number[] => {
  const options: number[] = [];
  for (let value = 0; value <= max; value += step) {
    options.push(value);
  }
  return options;
};

/**
 * Index of the option closest to `value`, or -1 when nothing is selected or the
 * column is empty. `options` is expected to ascend (as `buildOptions` builds
 * them), so the distance can only grow once it stopped shrinking: the search
 * stops there instead of walking all 60 options on every render.
 */
export const getNearestOptionIndex = (
  options: number[],
  value: number | undefined,
): number => {
  if (value === undefined || !options.length) return -1;

  let nearest = 0;
  let nearestDistance = Math.abs(options[0] - value);

  for (let index = 1; index < options.length; index += 1) {
    const distance = Math.abs(options[index] - value);
    if (distance >= nearestDistance) break;
    nearest = index;
    nearestDistance = distance;
  }

  return nearest;
};

/* #endregion */

/* #region input mask */

/**
 * Digits of the input, with every segment that the ":" already closed padded to
 * two ("1:30" -> "0130"). Without that padding, regrouping by pairs would shift
 * the digits into the wrong segments ("1:30:45" would read as "13:04:05").
 */
const extractSegmentDigits = (raw: string, segmentCount: number): string => {
  const parts = raw.split(":");
  return parts
    .map((part, index) => {
      const cleaned = part.replace(/\D/g, "");
      const isClosed = index < parts.length - 1;
      return isClosed && cleaned.length === 1 ? `0${cleaned}` : cleaned;
    })
    .join("")
    .slice(0, segmentCount * 2);
};

/**
 * Turns whatever the user typed into masked text ("093" -> "09:3").
 * Digits are regrouped by pairs, a ":" typed by the user finishes the current
 * segment ("9:" -> "09"), and every segment is clamped to its own maximum.
 */
export const formatTypedTimeText = (raw: string, maxima: number[]): string =>
  chunkDigits(extractSegmentDigits(raw, maxima.length))
    .map((group, index) => {
      const max = maxima[index];
      const numeric = Number(group);
      if (numeric <= max) return group;
      return group.length === 1 ? String(max) : pad2(max);
    })
    .join(":");

/** Completes partially entered text: "9" -> "09:00:00", "1:30" -> "01:30:00". */
export const normalizeTimeText = (
  text: string,
  segmentCount: number,
): string => {
  const digits = extractSegmentDigits(text, segmentCount);
  if (!digits) return "";

  const groups = chunkDigits(digits);
  return Array.from({ length: segmentCount }, (_, index) =>
    pad2(Number(groups[index] ?? 0)),
  ).join(":");
};

/*
 * The text is a fixed grid of digit slots: "HH:MM:SS" holds slots 0..5, and the
 * separators occupy no slot. Typing overwrites the slot under the caret instead
 * of inserting, which is what makes editing an already filled value work.
 */

/** Caret position in the text -> digit slot index. */
export const positionToSlot = (position: number): number =>
  position - Math.floor(position / SEGMENT_TEXT_LENGTH);

/** Digit slot index -> caret position in the text. */
export const slotToPosition = (slot: number): number =>
  slot + Math.floor(slot / SEGMENT_DIGITS);

/** Splits the text into one zero filled two digit group per segment. */
export const textToPairs = (text: string, segmentCount: number): string[] => {
  const digits = text.replace(/\D/g, "");
  return Array.from({ length: segmentCount }, (_, index) => {
    const pair = digits.slice(
      index * SEGMENT_DIGITS,
      (index + 1) * SEGMENT_DIGITS,
    );
    return pair.length === SEGMENT_DIGITS ? pair : pad2(Number(pair || 0));
  });
};

/** True once every segment holds two digits, i.e. the value can be emitted. */
export const isCompleteText = (text: string, segmentCount: number): boolean => {
  const groups = text.split(":");
  return (
    groups.length === segmentCount &&
    groups.every((group) => group.length === 2)
  );
};

/* #endregion */

/* #region text <-> segments */

export const segmentValuesFromText = (
  text: string,
  segments: TimeSegmentKey[],
): SegmentValues => {
  const groups = text.split(":");
  const values: SegmentValues = {};
  segments.forEach((key, index) => {
    const group = groups[index];
    if (group !== undefined && group !== "") {
      values[key] = Number(group);
    }
  });
  return values;
};

export const textFromSegmentValues = (
  values: SegmentValues,
  segments: TimeSegmentKey[],
  maxima: number[],
): string =>
  segments
    .map((key, index) => pad2(clamp(values[key] ?? 0, 0, maxima[index])))
    .join(":");

/**
 * Completes partial text and clamps every segment to its maximum, i.e. the text
 * a finished edit results in ("9" -> "09:00:00"). Empty input stays empty.
 */
export const normalizeToSegmentText = (
  text: string,
  segments: TimeSegmentKey[],
  maxima: number[],
): string => {
  const normalized = normalizeTimeText(text, segments.length);
  if (!normalized) return "";
  return textFromSegmentValues(
    segmentValuesFromText(normalized, segments),
    segments,
    maxima,
  );
};

/* #endregion */

/* #region output formats */

/** Splits a duration in milliseconds into the requested segments. */
export const segmentValuesFromMilliseconds = (
  milliseconds: number,
  segments: TimeSegmentKey[] = DEFAULT_SEGMENTS,
): SegmentValues => {
  const values: SegmentValues = {};
  let rest = Number.isFinite(milliseconds)
    ? Math.max(0, Math.round(milliseconds))
    : 0;

  if (segments.includes("hours")) {
    values.hours = Math.floor(rest / MS_IN_HOUR);
    rest -= values.hours * MS_IN_HOUR;
  }
  if (segments.includes("minutes")) {
    values.minutes = Math.floor(rest / MS_IN_MINUTE);
    rest -= values.minutes * MS_IN_MINUTE;
  }
  if (segments.includes("seconds")) {
    values.seconds = Math.floor(rest / MS_IN_SECOND);
  }

  return values;
};

/** Builds every output format from the entered segments. */
export const buildTimeValue = (
  values: SegmentValues,
  segments: TimeSegmentKey[] = DEFAULT_SEGMENTS,
): CustomTimeValue => {
  const hours = values.hours ?? 0;
  const minutes = values.minutes ?? 0;
  const seconds = values.seconds ?? 0;
  const milliseconds =
    hours * MS_IN_HOUR + minutes * MS_IN_MINUTE + seconds * MS_IN_SECOND;

  return {
    formatted: segments.map((key) => pad2(values[key] ?? 0)).join(":"),
    formattedHms: `${pad2(hours)}:${pad2(minutes)}:${pad2(seconds)}`,
    milliseconds,
    totalSeconds: milliseconds / MS_IN_SECOND,
    hours,
    minutes,
    seconds,
  };
};

/** Duration in milliseconds -> every output format. */
export const timeValueFromMilliseconds = (
  milliseconds: number,
  segments: TimeSegmentKey[] = DEFAULT_SEGMENTS,
): CustomTimeValue =>
  buildTimeValue(
    segmentValuesFromMilliseconds(milliseconds, segments),
    segments,
  );

/** "HH:MM:SS" string -> every output format. Returns undefined for empty input. */
export const timeValueFromString = (
  text: string,
  segments: TimeSegmentKey[] = DEFAULT_SEGMENTS,
): CustomTimeValue | undefined => {
  const normalized = normalizeTimeText(text, segments.length);
  if (!normalized) return undefined;
  return buildTimeValue(segmentValuesFromText(normalized, segments), segments);
};

/** Reads the `value` prop, which may be a "HH:MM:SS" string or milliseconds. */
export const textFromValueProp = (
  value: string | number | null | undefined,
  segments: TimeSegmentKey[],
  maxima: number[],
): string => {
  if (value === undefined || value === null || value === "") return "";

  if (typeof value === "number") {
    if (!Number.isFinite(value)) return "";
    return textFromSegmentValues(
      segmentValuesFromMilliseconds(value, segments),
      segments,
      maxima,
    );
  }

  return normalizeToSegmentText(value, segments, maxima);
};

/* #endregion */
